/**
 * frontend/web/src/components/admin/ProductValidation.jsx
 * Composant de validation des produits pour les administrateurs
 * Affiche les produits en pending, permet de les approuver ou rejeter
 */

import React, { useState, useEffect, useRef } from 'react';
import {
  Box, Typography, Card, CardContent, CardMedia, Grid, Button, Chip,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Pagination, FormControl, InputLabel, Select, MenuItem,
  Alert, CircularProgress, IconButton, Tooltip, Paper,
  Checkbox, Avatar, TablePagination
} from '@mui/material';
import {
  CheckCircle as ApproveIcon,
  Cancel as RejectIcon,
  Visibility as ViewIcon,
  Refresh as RefreshIcon,
  FilterList as FilterIcon
} from '@mui/icons-material';
import { useNotifications } from '../../context/NotificationContext';
import adminProductService from '../../services/adminProductService';
import restrictionAdminService from '../../services/restrictionAdminService';

const UPLOADS_BASE_URL =
  process.env.REACT_APP_API_URL?.replace('/api/v1', '') || 'http://localhost:5000';

const getImageUrl = (imagePath) => {
  if (!imagePath) return null;
  if (imagePath.startsWith('http')) return imagePath;
  return `${UPLOADS_BASE_URL}${imagePath.startsWith('/') ? imagePath : '/' + imagePath}`;
};

const PRODUCT_CONDITION_LABELS = {
  new: 'Neuf',
  used: 'Occasion'
};

export default function ProductValidation() {
  const { showNotification } = useNotifications();
  
  // États principaux
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const genRef = useRef(0);
  
  // États filtres
  const [filters, setFilters] = useState({
    status: 'pending',
    search: ''
  });
  
  // États modales
  const [approveDialog, setApproveDialog] = useState({ open: false, product: null });
  const [rejectDialog, setRejectDialog] = useState({ open: false, product: null, reason: '' });
  const [sellerUserInfo, setSellerUserInfo] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  
  // États sélection multiple
  const [selectedProducts, setSelectedProducts] = useState([]);

  /**
   * Charger les produits en attente
   */
  const fetchProducts = async (pageNum = page) => {
    const gen = ++genRef.current;
    setLoading(true);
    try {
      const response = await adminProductService.getPendingProducts({
        page: pageNum + 1,
        limit: rowsPerPage,
        status: filters.status || undefined,
        search: filters.search || undefined
      });
      
      if (gen !== genRef.current) return; // Réponse périmée
      
      setProducts(response.products || response.data || []);
      setTotalCount(response.totalCount || response.total || 0);
    } catch (error) {
      if (gen !== genRef.current) return;
      console.error('Erreur chargement produits:', error);
      showNotification('Erreur lors du chargement des produits', 'error');
      setProducts([]);
    } finally {
      if (gen !== genRef.current) return;
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProducts(0);
    setPage(0);
    setSelectedProducts([]);
  }, [filters, rowsPerPage]);

  /**
   * Ouvrir les détails d'un produit dans un nouvel onglet
   */
  const handleViewDetails = (product) => {
    const detailUrl = `${window.location.origin}/product/${product.id}`;
    window.open(detailUrl, '_blank');
  };

  /**
   * Charger les infos du vendeur
   */
  const loadSellerInfo = async (sellerId) => {
    if (!sellerId) {
      setSellerUserInfo(null);
      return;
    }
    try {
      const response = await restrictionAdminService.getUserBasicInfo(sellerId);
      setSellerUserInfo(response.data);
    } catch (err) {
      console.error('Failed to load seller info:', err);
      setSellerUserInfo(null);
    }
  };

  /**
   * Approuver un produit
   */
  const handleApprove = async (product) => {
    setSubmitting(true);
    try {
      await adminProductService.approveProduct(product.id);
      showNotification('Produit approuvé avec succès', 'success');
      setApproveDialog({ open: false, product: null });
      fetchProducts(page);
    } catch (error) {
      console.error('Erreur approbation:', error);
      const msg = error?.response?.data?.message || 'Erreur lors de l\'approbation';
      showNotification(msg, 'error');
    } finally {
      setSubmitting(false);
    }
  };

  /**
   * Rejeter un produit
   */
  const handleReject = async () => {
    const { product, reason } = rejectDialog;
    if (!product || !reason.trim()) {
      showNotification('Veuillez fournir un motif de rejet', 'warning');
      return;
    }
    
    if (reason.trim().length < 10) {
      showNotification('Le motif doit contenir au moins 10 caractères', 'warning');
      return;
    }
    
    setSubmitting(true);
    try {
      await adminProductService.rejectProduct(product.id, { reason: reason.trim() });
      showNotification('Produit rejeté avec succès', 'success');
      setRejectDialog({ open: false, product: null, reason: '' });
      fetchProducts(page);
    } catch (error) {
      console.error('Erreur rejet:', error);
      const msg = error?.response?.data?.message || 'Erreur lors du rejet';
      showNotification(msg, 'error');
    } finally {
      setSubmitting(false);
    }
  };

  /**
   * Gérer la sélection du checkbox
   */
  const handleSelectProduct = (productId) => {
    setSelectedProducts(prev =>
      prev.includes(productId)
        ? prev.filter(id => id !== productId)
        : [...prev, productId]
    );
  };

  /**
   * Sélectionner/désélectionner tous
   */
  const handleSelectAll = (event) => {
    if (event.target.checked) {
      setSelectedProducts(products.map(p => p.id));
    } else {
      setSelectedProducts([]);
    }
  };

  const isAllSelected = products.length > 0 && selectedProducts.length === products.length;

  return (
    <Box sx={{ width: '100%' }}>
      {/* ── Barre de filtres ─── */}
      <Paper elevation={0} sx={{ p: 2, mb: 3, borderRadius: 2, border: '1px solid', borderColor: 'divider', background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)' }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              size="small"
              fullWidth
              placeholder="Rechercher par titre..."
              value={filters.search}
              onChange={(e) => setFilters({ ...filters, search: e.target.value })}
              variant="outlined"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl size="small" fullWidth>
              <InputLabel>Statut</InputLabel>
              <Select
                value={filters.status}
                onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                label="Statut"
              >
                <MenuItem value="pending">En attente</MenuItem>
                <MenuItem value="active">Approuvé</MenuItem>
                <MenuItem value="rejected">Rejeté</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={() => fetchProducts(0)}
              fullWidth
            >
              Actualiser
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Compteur */}
      <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          {totalCount} produit{totalCount !== 1 ? 's' : ''} au total
        </Typography>
        {selectedProducts.length > 0 && (
          <Chip
            label={`${selectedProducts.length} sélectionné${selectedProducts.length !== 1 ? 's' : ''}`}
            color="primary"
            onDelete={() => setSelectedProducts([])}
          />
        )}
      </Box>

      {/* Loading */}
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      )}

      {/* Tableau des produits */}
      {!loading && products.length > 0 && (
        <>
          <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
            <Table size="small">
              <TableHead sx={{ background: 'linear-gradient(45deg, #6200ee 30%, #03dac4 90%)', color: 'white' }}>
                <TableRow>
                  <TableCell padding="checkbox">
                    <Checkbox
                      indeterminate={selectedProducts.length > 0 && selectedProducts.length < products.length}
                      checked={isAllSelected}
                      onChange={handleSelectAll}
                      sx={{ color: 'white' }}
                    />
                  </TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 600 }}>Image</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 600 }}>Titre</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 600 }}>Propriétaire</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 600 }}>Catégorie</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 600 }}>Prix</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 600 }}>État</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 600 }}>Variantes</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 600 }} align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {products.map((product) => {
                  const isSelected = selectedProducts.includes(product.id);
                  const thumb = product.images?.[0];
                  const thumbUrl = getImageUrl(thumb);
                  const priceFormatted = product.price_cents
                    ? `${(product.price_cents / 100).toFixed(2)} ${product.currency || 'EUR'}`
                    : '—';
                  
                  return (
                    <TableRow
                      key={product.id}
                      sx={{
                        bgcolor: isSelected ? 'action.selected' : 'inherit',
                        '&:hover': { bgcolor: 'action.hover' },
                        cursor: 'pointer'
                      }}
                    >
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={isSelected}
                          onChange={() => handleSelectProduct(product.id)}
                        />
                      </TableCell>
                      <TableCell>
                        {thumbUrl ? (
                          <Avatar
                            src={thumbUrl}
                            alt={product.title}
                            sx={{ width: 44, height: 44, borderRadius: 1 }}
                          />
                        ) : (
                          <Box
                            sx={{
                              width: 44,
                              height: 44,
                              borderRadius: 1,
                              background: 'linear-gradient(45deg, #6200ee 30%, #03dac4 90%)',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center'
                            }}
                          >
                            <Typography variant="caption" sx={{ color: 'white' }}>—</Typography>
                          </Box>
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight={500} noWrap sx={{ maxWidth: 150 }}>
                          {product.title}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption" color="text.secondary">
                          {product.owner?.full_name || product.owner?.email || '—'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption">{product.category?.name || '—'}</Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={priceFormatted}
                          size="small"
                          variant="outlined"
                          color="primary"
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={PRODUCT_CONDITION_LABELS[product.condition] || 'Non renseigné'}
                          size="small"
                          variant="outlined"
                          color={product.condition === 'new' ? 'success' : 'default'}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption">{product.variants?.length || 0}</Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title="Voir les détails">
                          <IconButton
                            size="small"
                            color="info"
                            onClick={() => handleViewDetails(product)}
                          >
                            <ViewIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Approuver">
                          <IconButton
                            size="small"
                            color="success"
                            onClick={() => {
                              setApproveDialog({ open: true, product });
                              loadSellerInfo(product.seller_id || product.seller?.id);
                            }}
                          >
                            <ApproveIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Rejeter">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => {
                              setRejectDialog({ open: true, product, reason: '' });
                              loadSellerInfo(product.seller_id || product.seller?.id);
                            }}
                          >
                            <RejectIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Pagination */}
          <TablePagination
            rowsPerPageOptions={[5, 10, 25, 50]}
            component="div"
            count={totalCount}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={(_, newPage) => {
              setPage(newPage);
              fetchProducts(newPage);
            }}
            onRowsPerPageChange={(event) => {
              setRowsPerPage(parseInt(event.target.value, 10));
              setPage(0);
            }}
            labelRowsPerPage="Produits par page"
          />
        </>
      )}

      {/* Aucun produit */}
      {!loading && products.length === 0 && (
        <Paper sx={{ p: 4, textAlign: 'center', borderRadius: 2 }}>
          <Typography variant="body1" color="text.secondary">
            Aucun produit en attente de validation
          </Typography>
        </Paper>
      )}

      {/* ── Dialog: Confirmation approbation ─── */}
      <Dialog open={approveDialog.open} onClose={() => {
        setApproveDialog({ open: false, product: null });
        setSellerUserInfo(null);
      }}>
        <DialogTitle sx={{ fontWeight: 700 }}>Approuver le produit</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          {sellerUserInfo && (
            <Alert severity="info" sx={{ mb: 2 }}>
              <Typography variant="body2">
                <strong>Vendeur:</strong> {sellerUserInfo.name || sellerUserInfo.email}
              </Typography>
              <Typography variant="body2">
                <strong>Email:</strong> {sellerUserInfo.email}
              </Typography>
              <Typography variant="body2">
                <strong>Langue de préférence:</strong> {sellerUserInfo.language.toUpperCase()}
              </Typography>
            </Alert>
          )}
          <Alert severity="info" sx={{ mb: 2 }}>
            Êtes-vous sûr de vouloir approuver le produit <strong>{approveDialog.product?.title}</strong> ?
          </Alert>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => {
            setApproveDialog({ open: false, product: null });
            setSellerUserInfo(null);
          }}>
            Annuler
          </Button>
          <Button
            variant="contained"
            color="success"
            onClick={() => handleApprove(approveDialog.product)}
            disabled={submitting}
            startIcon={submitting ? <CircularProgress size={16} /> : <ApproveIcon />}
          >
            Approuver
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Dialog: Rejet avec motif ─── */}
      <Dialog open={rejectDialog.open} onClose={() => {
        setRejectDialog({ open: false, product: null, reason: '' });
        setSellerUserInfo(null);
      }} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Rejeter le produit</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          {sellerUserInfo && (
            <Alert severity="info" sx={{ mb: 2 }}>
              <Typography variant="body2">
                <strong>Vendeur:</strong> {sellerUserInfo.name || sellerUserInfo.email}
              </Typography>
              <Typography variant="body2">
                <strong>Email:</strong> {sellerUserInfo.email}
              </Typography>
              <Typography variant="body2">
                <strong>Langue de préférence:</strong> {sellerUserInfo.language.toUpperCase()}
              </Typography>
            </Alert>
          )}
          <Alert severity="warning" sx={{ mb: 2 }}>
            Vous êtes sur le point de rejeter <strong>{rejectDialog.product?.title}</strong>
          </Alert>
          <TextField
            fullWidth
            multiline
            rows={4}
            label="Motif du rejet *"
            placeholder="Décrivez les raisons du rejet (minimum 10 caractères)..."
            value={rejectDialog.reason}
            onChange={(e) => setRejectDialog({ ...rejectDialog, reason: e.target.value })}
            variant="outlined"
            helperText={`${rejectDialog.reason.length} caractères (minimum 10)`}
            error={rejectDialog.reason.length > 0 && rejectDialog.reason.length < 10}
          />
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => {
            setRejectDialog({ open: false, product: null, reason: '' });
            setSellerUserInfo(null);
          }}>
            Annuler
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleReject}
            disabled={submitting || !rejectDialog.reason.trim() || rejectDialog.reason.length < 10}
            startIcon={submitting ? <CircularProgress size={16} /> : <RejectIcon />}
          >
            Rejeter
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
